var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Left Sidebar
 * Package management panel.
 */
import React, { useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchOutlined } from '@ant-design/icons';
import Button from '@fp-suite/core-ui/src/components/Button';
import { usePackageManagement, usePackingState } from '../../../hooks/packing';
import { CreatePackageDropdown } from './package/CreatePackageDropdown';
import { PackageDetailsModal } from '../modals/PackageDetailsModal';
import { PackageList } from './package/PackageList';
import { IoMdFlash } from 'react-icons/io';
export var LeftSidebar = function (_a) {
    var selectedPackageKey = _a.selectedPackageKey, virtualPackages = _a.virtualPackages, onPackageSelect = _a.onPackageSelect, onNewPackage = _a.onNewPackage, onBulkPackClick = _a.onBulkPackClick, _b = _a.showResetAllPackages, showResetAllPackages = _b === void 0 ? false : _b, _c = _a.showDeleteAllPackages, showDeleteAllPackages = _c === void 0 ? false : _c, _d = _a.isResetAllPackagesLoading, isResetAllPackagesLoading = _d === void 0 ? false : _d, _e = _a.isDeleteAllPackagesLoading, isDeleteAllPackagesLoading = _e === void 0 ? false : _e, onResetAllPackages = _a.onResetAllPackages, onDeleteAllPackages = _a.onDeleteAllPackages;
    var t = useTranslation().t;
    var _f = React.useState(false), browseModalVisible = _f[0], setBrowseModalVisible = _f[1];
    var getAllPackagesInfo = usePackageManagement().getAllPackagesInfo;
    var _g = usePackingState(), boxTypes = _g.boxTypes, shipmentItemDetailList = _g.shipmentItemDetailList, productSkuUpcList = _g.productSkuUpcList, packageContentBySeqId = _g.packageContentBySeqId, packageSerialNumbersBySeqId = _g.packageSerialNumbersBySeqId, loadBoxTypes = _g.loadBoxTypes;
    var serializedProductIds = useMemo(function () {
        var serializedIds = new Set();
        productSkuUpcList.forEach(function (item) {
            if ((item === null || item === void 0 ? void 0 : item.serialized) === 'Y' && (item === null || item === void 0 ? void 0 : item.productId)) {
                serializedIds.add(item.productId);
            }
        });
        shipmentItemDetailList.forEach(function (item) {
            if ((item === null || item === void 0 ? void 0 : item.serialized) === 'Y' && (item === null || item === void 0 ? void 0 : item.productId)) {
                serializedIds.add(item.productId);
            }
        });
        return serializedIds;
    }, [productSkuUpcList, shipmentItemDetailList]);
    var realPackages = useMemo(function () { return getAllPackagesInfo(); }, [getAllPackagesInfo]);
    var packageItems = useMemo(function () {
        var virtualItems = virtualPackages.map(function (virtualPackage) {
            var mappedBoxType = boxTypes.find(function (boxType) { return boxType.key === virtualPackage.shipmentBoxTypeId; });
            return {
                key: "virtual:" + virtualPackage.tempId,
                kind: 'virtual',
                displayId: t('New Package') + " " + virtualPackage.displayPackageNumber,
                virtualTempId: virtualPackage.tempId,
                shipmentBoxTypeId: virtualPackage.shipmentBoxTypeId,
                boxTypeDescription: mappedBoxType === null || mappedBoxType === void 0 ? void 0 : mappedBoxType.value,
                isClosed: false,
                itemCount: 0
            };
        });
        var realItems = realPackages.map(function (pkg) { return ({
            key: "real:" + pkg.shipmentPackageSeqId,
            kind: 'real',
            displayId: "#" + pkg.shipmentPackageSeqId,
            shipmentPackageSeqId: pkg.shipmentPackageSeqId,
            shipmentBoxTypeId: pkg.shipmentBoxTypeId,
            boxTypeDescription: pkg.boxTypeDescription,
            isClosed: pkg.isClosed,
            itemCount: pkg.itemCount,
            hasSerializedItems: (packageSerialNumbersBySeqId[pkg.shipmentPackageSeqId] || []).length > 0 ||
                (packageContentBySeqId[pkg.shipmentPackageSeqId] || []).some(function (item) {
                    return serializedProductIds.has(item.productId);
                })
        }); });
        return __spreadArray(__spreadArray([], virtualItems, true), realItems, true);
    }, [
        boxTypes,
        realPackages,
        t,
        virtualPackages,
        packageSerialNumbersBySeqId,
        packageContentBySeqId,
        serializedProductIds
    ]);
    useEffect(function () {
        if (boxTypes.length === 0) {
            void loadBoxTypes();
        }
    }, [boxTypes.length, loadBoxTypes]);
    var handleSelect = useCallback(function (item) {
        if (item.kind === 'real' && item.shipmentPackageSeqId) {
            onPackageSelect({ kind: 'real', seqId: item.shipmentPackageSeqId }, item.isClosed);
        }
        else if (item.kind === 'virtual' && item.virtualTempId) {
            onPackageSelect({ kind: 'virtual', tempId: item.virtualTempId }, false);
        }
        setBrowseModalVisible(false);
    }, [onPackageSelect]);
    var handleSelectBySeqId = useCallback(function (seqId) {
        var _a;
        var existingPackage = realPackages.find(function (realPackage) { return realPackage.shipmentPackageSeqId === seqId; });
        onPackageSelect({ kind: 'real', seqId: seqId }, (_a = existingPackage === null || existingPackage === void 0 ? void 0 : existingPackage.isClosed) !== null && _a !== void 0 ? _a : false);
        setBrowseModalVisible(false);
    }, [onPackageSelect, realPackages]);
    var handleNewPackage = useCallback(function (boxTypeId) {
        onNewPackage(boxTypeId);
    }, [onNewPackage]);
    return (_jsxs("div", __assign({ className: 'bg-white rounded-xl shadow-sm flex flex-col h-full min-h-0 w-full overflow-hidden' }, { children: [_jsx("div", __assign({ className: 'flex-1 min-h-0' }, { children: _jsx(PackageList, { items: packageItems, selectedPackageKey: selectedPackageKey, onSelect: handleSelect }, void 0) }), void 0), _jsxs("div", __assign({ className: 'flex flex-col gap-2 border-t border-gray-200 p-3' }, { children: [_jsx(CreatePackageDropdown, { boxTypes: boxTypes, onCreatePackage: handleNewPackage }, void 0), _jsxs("button", __assign({ type: 'button', className: 'flex flex-row items-center justify-center gap-1.5 text-sm text-primary font-semibold px-3 py-2.5 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors', onClick: onBulkPackClick }, { children: [_jsx(IoMdFlash, {}, void 0), " ", t('Bulk Pack')] }), void 0), _jsxs("div", __assign({ className: 'flex flex-row items-center justify-between gap-2' }, { children: [(showResetAllPackages || showDeleteAllPackages) && (_jsxs("div", __assign({ className: 'flex flex-1' }, { children: [showResetAllPackages && (_jsx(Button, { buttonText: t('Reset All Pkg'), paddingX: 'px-3', paddingY: 'py-2.5', onClick: function () {
                                            if (onResetAllPackages) {
                                                void onResetAllPackages();
                                            }
                                        }, loading: isResetAllPackagesLoading, defaultColor: 'bg-red-500', classNames: 'rounded-lg text-sm w-full', widthFull: true }, void 0)), showDeleteAllPackages && (_jsx(Button, { buttonText: t('Delete All Pkg'), paddingX: 'px-3', paddingY: 'py-2.5', onClick: function () {
                                            if (onDeleteAllPackages) {
                                                void onDeleteAllPackages();
                                            }
                                        }, loading: isDeleteAllPackagesLoading, defaultColor: 'bg-red-500', classNames: 'rounded-lg text-sm w-full', widthFull: true }, void 0))] }), void 0)), _jsxs("button", __assign({ type: 'button', className: 'flex flex-1 items-center justify-center gap-1.5 text-sm text-primary font-semibold px-3 py-2.5 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors', onClick: function () { return setBrowseModalVisible(true); } }, { children: [_jsx(SearchOutlined, { style: { fontSize: 14 } }, void 0), t('Package Details')] }), void 0)] }), void 0)] }), void 0), _jsx(PackageDetailsModal, { visible: browseModalVisible, onClose: function () { return setBrowseModalVisible(false); }, onPackageSelect: handleSelectBySeqId }, void 0)] }), void 0));
};
export default LeftSidebar;
